import { Rarity, Tag, type ModDef } from '../../sim/mods';
import { Stat } from '../../sim/stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Mods offensifs.

   Règle de rédaction : le texte de carte est TOUJOURS chiffré. « +18 % de
   cadence », jamais « tir plus rapide ». Un joueur qui ne peut pas comparer
   deux cartes ne fait pas un choix — il devine, et il finit par prendre au
   hasard, ce qui vide le système de son intérêt.

   Les mods qui portent DEUX tags sont les charnières du build : ils font
   progresser deux voies à la fois et créent les bifurcations intéressantes.

   ⚠️ RÈGLE DE RARETÉ, révisée au jalon J12 quand le pool est passé de 29 à 49
   ordinaires. Le taux d'apparition d'un mod vaut à peu près `1 − (1 − w/W)²⁰`,
   où W est la somme des poids du pool. En doublant le pool on double W, et le
   palier ÉPIQUE (poids 34) tombe sous les 15 % d'apparition — c'est-à-dire sous
   le seuil que le banc d'équilibrage qualifie lui-même de contenu mort.
   Mesuré : « Canons jumeaux » descendait à 15 % avec le pool élargi.

   La conséquence n'est pas d'assouplir le seuil, c'est que la TAILLE DU POOL
   fournit désormais la rareté que le palier Épique fournissait à 29 mods. Un
   mod fort se distingue par son `maxStacks` et par son coût, plus par un poids
   de tirage trois fois plus faible que les autres. Les trois Épiques d'origine
   sont donc passés en RARE ; leur fréquence réelle bouge à peine (22-30 % avant,
   28-34 % après), parce que la dilution compense presque exactement la promotion.
   ═══════════════════════════════════════════════════════════════════════════ */

const mods: ModDef[] = [
  /* ── CINÉTIQUE — la voie du volume de feu ─────────────────────────────── */
  {
    id: 'canons-jumeaux',
    name: 'Canons jumeaux',
    rarity: Rarity.Rare,
    tags: [Tag.Cinetique],
    // Un projectile de plus sur une base de deux, c'est +50 % de DPS d'un coup.
    // D'où le plafond : sans lui, ce mod éclipserait tout le reste du pool et il
    // n'y aurait plus de build, juste une file d'attente. La rareté, elle, ne
    // sert plus à le freiner — voir la règle de rareté en tête de fichier.
    // ⚠️ Plafond ramené de 3 à 2 exemplaires au jalon J12, et c'est une
    // conséquence directe du passage en Rare. Le banc est formel : `bulletCount`
    // est ADDITIF, donc il gouverne à lui seul la dispersion des runs — le
    // décile pauvre finit à 2 projectiles, le décile riche à 6. Tirer ce mod
    // plus souvent sans rabaisser son plafond faisait passer le rapport
    // p90/p10 de 3,99 à 4,57, au-delà du garde-fou. Ce n'est pas le seuil qui
    // avait tort : un écart de puissance de ×4,6 entre deux parties au même
    // niveau, c'est la sensation d'avoir perdu au tirage avant d'avoir joué.
    maxStacks: 2,
    modifiers: [{ stat: Stat.BulletCount, op: 'add', value: 1 }],
    // Le libellé nomme ce qu'on VOIT. Au-delà des deux canons de fuselage, un
    // projectile de plus, c'est un drone de plus dans la formation.
    text: '+1 drone d’escorte',
  },
  {
    id: 'calibre-lourd',
    name: 'Calibre lourd',
    rarity: Rarity.Common,
    tags: [Tag.Cinetique],
    modifiers: [{ stat: Stat.Damage, op: 'mul', value: 0.2 }],
    text: '+20 % de dégâts',
  },
  {
    id: 'percuteur',
    name: 'Percuteur renforcé',
    rarity: Rarity.Common,
    tags: [Tag.Cinetique],
    modifiers: [{ stat: Stat.FireRate, op: 'mul', value: 0.16 }],
    text: '+16 % de cadence',
  },
  {
    id: 'chargeur-etendu',
    name: 'Chargeur étendu',
    rarity: Rarity.Rare,
    tags: [Tag.Cinetique, Tag.Thermique],
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.12 },
      { stat: Stat.FireRate, op: 'mul', value: 0.11 },
    ],
    text: '+12 % de dégâts, +11 % de cadence',
  },

  /* ── PLASMA — la voie de la puissance par tir ─────────────────────────── */
  {
    id: 'surtension',
    name: 'Surtension',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma],
    modifiers: [{ stat: Stat.Damage, op: 'mul', value: 0.24 }],
    text: '+24 % de dégâts',
  },
  {
    id: 'accelerateur',
    name: 'Accélérateur de particules',
    rarity: Rarity.Common,
    tags: [Tag.Plasma],
    modifiers: [
      { stat: Stat.BulletSpeed, op: 'mul', value: 0.25 },
      { stat: Stat.FireRate, op: 'mul', value: 0.11 },
    ],
    text: '+25 % de vitesse de tir, +11 % de cadence',
  },
  {
    id: 'faisceau-large',
    name: 'Faisceau large',
    rarity: Rarity.Common,
    tags: [Tag.Plasma],
    // Un tir plus large touche plus souvent sans rien changer au DPS théorique.
    // C'est un mod dont la valeur réelle dépasse la valeur affichée — et c'est
    // volontaire : tout ne doit pas être comparable au chiffre près, sinon le
    // choix devient un calcul.
    modifiers: [{ stat: Stat.BulletRadius, op: 'mul', value: 0.4 }],
    text: '+40 % de largeur de tir',
  },

  /* ── THERMIQUE — la voie de la cadence ────────────────────────────────── */
  {
    id: 'chambre-chaude',
    name: 'Chambre chaude',
    rarity: Rarity.Common,
    tags: [Tag.Thermique],
    modifiers: [{ stat: Stat.Damage, op: 'mul', value: 0.18 }],
    text: '+18 % de dégâts',
  },
  {
    id: 'cadence-thermique',
    name: 'Cycle thermique',
    rarity: Rarity.Rare,
    tags: [Tag.Thermique],
    modifiers: [{ stat: Stat.FireRate, op: 'mul', value: 0.2 }],
    text: '+20 % de cadence',
  },
  {
    id: 'dissipateur',
    name: 'Dissipateur',
    rarity: Rarity.Common,
    tags: [Tag.Thermique, Tag.Orbital],
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.12 },
      { stat: Stat.MoveSpeed, op: 'mul', value: 0.08 },
    ],
    text: '+12 % de cadence, +8 % de vitesse',
  },

  /* ── DRONE — la voie du nombre ────────────────────────────────────────── */
  {
    id: 'drone-escorte',
    name: 'Drone d’escorte',
    rarity: Rarity.Rare,
    tags: [Tag.Drone],
    maxStacks: 2,
    modifiers: [{ stat: Stat.BulletCount, op: 'add', value: 1 }],
    text: '+1 drone d’escorte, qui tire avec toi',
  },
  {
    id: 'essaim',
    name: 'Essaim',
    rarity: Rarity.Rare,
    tags: [Tag.Drone, Tag.Cinetique],
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.14 },
      { stat: Stat.Damage, op: 'mul', value: 0.1 },
    ],
    text: '+14 % de cadence, +10 % de dégâts',
  },

  /* ── SURCHARGE — le risque assumé ─────────────────────────────────────── */
  {
    id: 'surchauffe',
    name: 'Surchauffe',
    rarity: Rarity.Rare,
    tags: [Tag.Surcharge, Tag.Thermique],
    // ⚠️ Un malus, mais JAMAIS un piège : le gain domine largement. Le plan
    // l'interdit explicitement — aucun mod ne doit être mort. Le malus existe
    // pour créer une tension de build, pas pour punir qui l'a pris.
    // ⚠️ Le malus porte sur la LARGEUR du tir, pas sur les dégâts, et le banc
    // d'équilibrage explique pourquoi. Un malus sur le même axe qu'un plafond
    // peut se retrouver seul à s'appliquer quand ce plafond mord : la carte
    // devient alors strictement nuisible, ce que le plan interdit (§6.7).
    // Un tir plus étroit est un vrai compromis — cadence contre précision — et
    // il ne peut jamais faire baisser le DPS théorique.
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.3 },
      { stat: Stat.BulletRadius, op: 'mul', value: -0.18 },
    ],
    text: '+30 % de cadence, −18 % de largeur de tir',
  },
  {
    id: 'reacteur-critique',
    name: 'Réacteur critique',
    rarity: Rarity.Rare,
    tags: [Tag.Surcharge, Tag.Plasma],
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.42 },
      { stat: Stat.MoveSpeed, op: 'mul', value: -0.12 },
    ],
    text: '+42 % de dégâts, −12 % de vitesse',
  },
  {
    id: 'munitions-instables',
    name: 'Munitions instables',
    rarity: Rarity.Common,
    tags: [Tag.Surcharge],
    modifiers: [{ stat: Stat.Damage, op: 'mul', value: 0.22 }],
    text: '+22 % de dégâts',
  },

  /* ── J12 — les spécialistes ───────────────────────────────────────────────
     Les mods ci-dessus sont des renforts génériques : ils vont dans n'importe
     quel build. Ceux qui suivent paient leur puissance sur un autre axe. Le
     coût porte TOUJOURS sur une statistique absente de la formule de DPS
     (largeur, vitesse de tir, déplacement, frôlement, cellules, vies, bombes) —
     pas par timidité, mais parce qu'un malus sur `damage`, `fireRate` ou
     `bulletCount` pourrait faire BAISSER le DPS d'un build, ce que le banc
     interdit à juste titre : un niveau qui rend plus faible est une punition
     pour avoir survécu. L'invariant est vérifié dans tests/balance/pool.test.ts.

     ⚠️ ET C'EST POURQUOI LES CARTES SANS COÛT CI-DESSUS ONT ÉTÉ RABAISSÉES.
     L'arbitrage est arithmétique, pas esthétique. Le nombre de cartes prises
     dans une run est fixe — vingt — donc la courbe de puissance impose la
     puissance MOYENNE d'une carte, quel que soit le nombre de cartes du pool.
     Une carte payante ne peut donc dépasser une carte gratuite que si la carte
     gratuite descend d'autant. Sans ce rabaissement, chacun des spécialistes
     ci-dessous se retrouvait strictement dominé par un mod d'origine — même
     effet, sans le coût — c'est-à-dire vingt cartes mortes ajoutées d'un coup.

     Les valeurs d'origine ont donc perdu 4 à 6 points (« +30 % de dégâts » →
     « +24 % »), et les spécialistes se placent 6 à 10 points au-dessus. La
     médiane du banc, elle, ne bouge pas : c'est la répartition qui change, pas
     le budget total.
     ─────────────────────────────────────────────────────────────────────── */
  {
    id: 'chambre-a-vide',
    name: 'Chambre à vide',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Surcharge],
    maxStacks: 2,
    // Le prix est payé en SCORE et en marge de sécurité, pas en puissance : le
    // pilote qui prend cette carte renonce à jouer près des projectiles. C'est
    // l'arbitrage inverse de « Champ de frôlement », et les deux doivent rester
    // simultanément défendables.
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.32 },
      { stat: Stat.GrazeRadius, op: 'mul', value: -0.25 },
    ],
    text: '+32 % de dégâts, −25 % de rayon de frôlement',
  },
  {
    id: 'resonateur',
    name: 'Résonateur',
    rarity: Rarity.Rare,
    tags: [Tag.Plasma, Tag.Thermique],
    maxStacks: 2,
    // Des tirs plus lents se laissent distancer par un ennemi qui traverse
    // l'écran : le DPS théorique monte, le DPS réel dépend du pilotage. C'est
    // exactement le genre d'écart que le banc ne mesure pas et que le playtest
    // tranchera.
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.26 },
      { stat: Stat.BulletSpeed, op: 'mul', value: -0.22 },
    ],
    text: '+26 % de cadence, −22 % de vitesse de tir',
  },
  {
    id: 'injection-forcee',
    name: 'Injection forcée',
    rarity: Rarity.Rare,
    tags: [Tag.Thermique, Tag.Surcharge],
    // Un exemplaire au maximum : la bombe est le filet anti-frustration du jeu
    // (§6.5). En vendre une est un choix fort ; en vendre deux transformerait la
    // carte en piège pour un joueur qui ne mesure pas ce qu'il abandonne.
    maxStacks: 1,
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.3 },
      { stat: Stat.MaxBombs, op: 'add', value: -1 },
    ],
    text: '+30 % de cadence, −1 bombe',
  },
  {
    id: 'barillet-rotatif',
    name: 'Barillet rotatif',
    rarity: Rarity.Rare,
    tags: [Tag.Cinetique, Tag.Thermique],
    maxStacks: 2,
    modifiers: [
      { stat: Stat.FireRate, op: 'mul', value: 0.18 },
      { stat: Stat.Damage, op: 'mul', value: 0.1 },
      { stat: Stat.MoveSpeed, op: 'mul', value: -0.1 },
    ],
    text: '+18 % de cadence, +10 % de dégâts, −10 % de vitesse de déplacement',
  },
  {
    id: 'amorce-plasma',
    name: 'Amorce plasma',
    rarity: Rarity.Common,
    tags: [Tag.Plasma],
    maxStacks: 3,
    modifiers: [
      { stat: Stat.Damage, op: 'mul', value: 0.1 },
      { stat: Stat.BulletRadius, op: 'mul', value: 0.22 },
    ],
    text: '+10 % de dégâts, +22 % de largeur de tir',
  },
  {
    id: 'essaim-dense',
    name: 'Essaim dense',
    rarity: Rarity.Rare,
    tags: [Tag.Drone, Tag.Cinetique],
    maxStacks: 2,
    // ⚠️ MULTIPLICATIF sur le nombre de projectiles, et c'est toute l'idée : le
    // moteur arrondit `bulletCount` à l'inférieur, donc +18 % ne rend rien sur
    // une volée de deux et rend un projectile de plus dès six. C'est le premier
    // mod du pool qui exige un build DÉJÀ constitué au lieu d'être bon partout —
    // il ouvre une fin de voie plutôt qu'un début.
    // Le +8 % de cadence est là pour qu'il ne soit jamais une carte vide : le
    // plan interdit le mod mort, y compris temporairement.
    modifiers: [
      { stat: Stat.BulletCount, op: 'mul', value: 0.18 },
      { stat: Stat.FireRate, op: 'mul', value: 0.08 },
    ],
    text: '+18 % de projectiles (arrondi à l’inférieur), +8 % de cadence',
  },
];

export default mods;
